/**
 * IntegrationsTab Component
 * Manages third-party integrations (Squadline, Stripe, etc.)
 */
import "./IntegrationsTab.css";
import { useEffect, useState } from "react";
import { AlertCircle, CreditCard, Loader2, Mail, Store } from "lucide-react";
import SquadlineIntegration from "../../components/SquadlineIntegration";
import { axiosInstance } from "../../App";

const IntegrationsTab = ({ 
  organization,
  stripeConnected
}) => {
  const [marketplaceApps, setMarketplaceApps] = useState([]);
  const [marketplaceLoading, setMarketplaceLoading] = useState(true);
  const [marketplaceError, setMarketplaceError] = useState(null);

  const loadMarketplaceApps = () => {
    setMarketplaceLoading(true);
    setMarketplaceError(null);
    return axiosInstance.get("/marketplace/apps")
      .then(({ data }) => setMarketplaceApps(data.apps || []))
      .catch(() => {
        setMarketplaceApps([]);
        setMarketplaceError("We couldn't load the app marketplace. Try again.");
      })
      .finally(() => setMarketplaceLoading(false));
  };

  useEffect(() => {
    let mounted = true;
    setMarketplaceLoading(true);
    setMarketplaceError(null);
    axiosInstance.get("/marketplace/apps")
      .then(({ data }) => mounted && setMarketplaceApps(data.apps || []))
      .catch(() => {
        if (mounted) {
          setMarketplaceApps([]);
          setMarketplaceError("We couldn't load the app marketplace. Try again.");
        }
      })
      .finally(() => mounted && setMarketplaceLoading(false));
    return () => { mounted = false; };
  }, []);

  const squadlineApp = marketplaceApps.find((app) => app.slug === "squadline");

  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Store className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">App Marketplace</h3>
            <p className="text-sm text-slate-500">
              Install optional apps for your organization. Apps only receive the permissions and data shown here.
            </p>
          </div>
        </div>
        {marketplaceLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500" role="status">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading marketplace…
          </div>
        ) : marketplaceError ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="w-4 h-4" />
              {marketplaceError}
            </div>
            <button
              type="button"
              onClick={loadMarketplaceApps}
              className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Retry
            </button>
          </div>
        ) : (
          <SquadlineIntegration organization={organization} marketplaceApp={squadlineApp} />
        )}
        {squadlineApp && (
          <div className="grid gap-4 mt-4 pt-4 border-t border-slate-100 md:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Permissions</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-600">
                {(squadlineApp.permissions || []).map((permission) => <li key={permission}>• {permission}</li>)}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data shared</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-600">
                {(squadlineApp.data_shared || []).map((item) => <li key={item}>• {item}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>
      
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">Platform Connections</h3>
        
        <div className="space-y-4">
          {/* Stripe */}
          <div className="flex items-center justify-between p-4 border border-slate-200/60 rounded-2xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Stripe</p>
                <p className="text-sm text-slate-500">Payment processing</p>
              </div>
            </div>
            <span className={`px-3 py-1 text-xs rounded-full ${
              stripeConnected ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
            }`}>
              {stripeConnected ? 'Connected' : 'Not Connected'}
            </span>
          </div>

          {/* Google Calendar - Coming Soon */}
          <div className="flex items-center justify-between p-4 border border-slate-200/60 rounded-2xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-slate-900">Google Calendar</p>
                <p className="text-sm text-slate-500">Sync schedules with Google Calendar</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
              Coming Soon
            </span>
          </div>

          {/* Email Service - Coming Soon */}
          <div className="flex items-center justify-between p-4 border border-slate-200/60 rounded-2xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Email Service</p>
                <p className="text-sm text-slate-500">Send automated emails</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
              Coming Soon
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsTab;
